import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import { Container, Box, Button, CircularProgress, Select, MenuItem, FormControl, InputLabel, Typography, Grid, Paper } from '@mui/material';
import PatientDropdown from '../components/common/PatientDropdown';
import ProtocolDropdown from '../components/common/ProtocolDropdown';
import FilterDropdown from '../components/common/filterDropdown';
import PatientContext from '../components/common/patientContext';
import '../assets/styles/ProtocolView.css'; // Updated import path

// Define custom styles for components
const useStyles = {
  button: {
    backgroundColor: '#3B3B3D', // Grey color
    color: 'white',
    '&:hover': {
      backgroundColor: '#019FE2', // Light blue on hover
    },
    width: '100%',
    padding: '10px',
    fontSize: '1.2rem',
  },
  outlinedButton: {
    backgroundColor: '#3B3B3D',
    color: 'white',
    border: '1px solid #019FE2',
    '&:hover': {
      backgroundColor: '#019FE2',
      color: 'white',
      border: '1px solid #019FE2',
    },
    width: '150px',
    height: '50px',
    fontSize: '1.2rem',
  },
  statBox: {
    backgroundColor: '#019FE2',
    color: 'white',
    padding: '16px',
    borderRadius: '16px', // Increase border radius for a more rounded look
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)', // Enhanced shadow
  },
  plotContainer: {
    backgroundColor: '#3B3B3D',
    color: 'white',
    padding: '16px',
    borderRadius: '16px', // Increase border radius for a more rounded look
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)', // Enhanced shadow
    marginBottom: '16px',
  },
  statsContainer: {
    backgroundColor: '#3B3B3D',
    padding: '16px',
    borderRadius: '16px', // Increase border radius for a more rounded look
    marginTop: '16px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)', // Enhanced shadow
  },
  formControl: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%', // Take full width of the flex container
  },
  select: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#019FE2',
    },
  },
};

function ProtocolView() {
  // State variables
  const [protocols, setProtocols] = useState([]);
  const [selectedProtocol, setSelectedProtocol] = useState('');
  const [protocolData, setProtocolData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [nonImuData, setNonImuData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStepIndex, setSelectedStepIndex] = useState(0);
  const { selectedPatient, setSelectedPatient } = useContext(PatientContext);
  const [stepFilter, setStepFilter] = useState('');

  // Fetch protocol data when selectedProtocol changes
  useEffect(() => {
    if (selectedProtocol) {
      handleGetProtocolData();
    }
  }, [selectedProtocol]);

  // Filter protocol data when stepFilter changes
  useEffect(() => {
    if (stepFilter) {
      const filtered = protocolData.filter(step => step.stepname.includes(stepFilter));
      setFilteredData(filtered);
    } else {
      setFilteredData(protocolData);
    }
  }, [stepFilter, protocolData]);

  // Handle patient selection change
  const handlePatientChange = (selection) => {
    setSelectedPatient(selection);
    setProtocols([]);
    setSelectedProtocol('');
  };

  // Handle protocol selection change
  const handleProtocolChange = (selection) => {
    setSelectedProtocol(selection);
  };

  // Handle step filter change
  const handleFilterChange = (filter) => {
    setStepFilter(filter);
  };

// Fetch protocol data from the server
const handleGetProtocolData = () => {
  console.log("Fetching protocol data for ID:", selectedProtocol);  // Debugging log
  setIsLoading(true);
  // axios.post('http://127.0.0.1:5000/api/get_protocol', { protocolId: selectedProtocol })
  axios.post('https://dashboard.encora-internal.com/api/get_protocol', { protocolId: selectedProtocol })
    .then(response => {
      const data = response.data;
      console.log("Received protocol data:", data);  // Debugging log
      if (data) {
        const protocolDataArray = Object.values(data).sort((a, b) => a.steporder - b.steporder);
        setProtocolData(protocolDataArray);
        setFilteredData(protocolDataArray);
        setSelectedStepIndex(0);
      }
      setIsLoading(false);
    })
    .catch(error => {
      console.error('Error fetching protocol data:', error);
      setIsLoading(false);
    });
};

  // Handle protocol data download
  const handleDownloadProtocol = () => {  
    // axios.post('http://127.0.0.1:5000/api/download_protocol', { protocolId: selectedProtocol }, { responseType: 'blob' })
    axios.post('https://dashboard.encora-internal.com/api/download_protocol', { protocolId: selectedProtocol }, { responseType: 'blob' })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `protocol_${selectedProtocol}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(error => {
        console.error('Error downloading protocol data:', error);
      });
  };

  // Navigate to the previous step
  const handlePreviousStep = () => {
    setSelectedStepIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
  };

  // Navigate to the next step
  const handleNextStep = () => {
    setSelectedStepIndex((prevIndex) => (prevIndex < filteredData.length - 1 ? prevIndex + 1 : prevIndex));
  };

  return (
    <Container maxWidth={false} className="protocol-page">
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4}>
        {/* Dropdowns for selecting patient, protocol, and filter */}
        <Box display="flex" width="100%" mb={2} justifyContent="space-between">
          <Box flex={1} mx={1}>
            <FormControl variant="outlined" style={useStyles.formControl}>
              <InputLabel></InputLabel>
              <PatientDropdown onChange={handlePatientChange} style={useStyles.select} />
            </FormControl>
          </Box>
          <Box flex={1} mx={1}>
            <FormControl variant="outlined" style={useStyles.formControl}>
              <InputLabel></InputLabel>
              <ProtocolDropdown selectedPatient={selectedPatient} onChange={handleProtocolChange} style={useStyles.select} />
            </FormControl>
          </Box>
          <Box flex={1} mx={1}>
            <FormControl variant="outlined" style={useStyles.formControl}>
              <InputLabel></InputLabel>
              <FilterDropdown onChange={handleFilterChange} style={useStyles.select} />
            </FormControl>
          </Box>
        </Box>
        {/* Button to fetch protocol data */}
        <Box width="100%" mb={2}>
          <Button variant="contained" style={useStyles.button} onClick={handleGetProtocolData}>
            Get Protocol Data
          </Button>
        </Box>
        {/* Button to download protocol data */}
        <Box width="100%" mb={2}>
          <Button variant="contained" style={useStyles.button} onClick={handleDownloadProtocol}>
            Download Protocol Data as CSV
          </Button>
        </Box>
        {isLoading ? (
          <div className="loader-wrapper">
            <CircularProgress className="loader" />
          </div>
        ) : (
          <>
            {Array.isArray(filteredData) && filteredData.length > 0 && (
              <>
                {/* Navigation for previous and next steps */}
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" my={2}>
                  <Button variant="outlined" style={useStyles.outlinedButton} onClick={handlePreviousStep}>Previous</Button>
                  <FormControl variant="outlined" style={{ ...useStyles.formControl, width: '200px', height: '50px' }}>
                    <InputLabel>Step</InputLabel>
                    <Select value={selectedStepIndex} onChange={(e) => setSelectedStepIndex(Number(e.target.value))} style={useStyles.select}>
                      {filteredData.map((_, index) => (
                        <MenuItem key={index} value={index}>Step {index + 1}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="outlined" style={useStyles.outlinedButton} onClick={handleNextStep}>Next</Button>
                </Box>
                {/* Plot for the selected step */}
                {filteredData[selectedStepIndex] && (
                  <SinglePlot key={filteredData[selectedStepIndex].steporder} {...filteredData[selectedStepIndex]} />
                )}
              </>
            )}
          </>
        )}
        {/* Display non-IMU data */}
        {nonImuData && (
          <Box mt={4} width="100%">
            <Typography variant="h5" gutterBottom>Non-IMU Data</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Paper elevation={3} style={useStyles.statBox}>
                  <Typography variant="body1">{`ACCELFFTAMPLITUDE: ${nonImuData.ACCELFFTAMPLITUDE}`}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper elevation={3} style={useStyles.statBox}>
                  <Typography variant="body1">{`ACCELFFTFREQUENCY: ${nonImuData.ACCELFFTFREQUENCY}`}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper elevation={3} style={useStyles.statBox}>
                  <Typography variant="body1">{`GYROFFTAMPLITUDE: ${nonImuData.GYROFFTAMPLITUDELIST}`}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper elevation={3} style={useStyles.statBox}>
                  <Typography variant="body1">{`GYROFFTFREQUENCY: ${nonImuData.GYROFFTFREQUENCY}`}</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </Container>
  );
}

// Component to display a single plot
function SinglePlot({ steporder, stepname, rawx, rawy, rawz, rawpitch, rawroll, rawyaw, datetimeutc, sampling_frequency, num_samples_per_second, avg_gyrofft_amplitude, avg_gyrofft_frequency, avg_accelfft_amplitude, avg_accelfft_frequency }) {
  const classes = useStyles;
  return (
    <Box width="100%" mt={4}>
      <Box p={2} border={1} borderColor="grey.400" borderRadius={4} style={classes.plotContainer}>
        <Typography variant="h4" align="center" gutterBottom style={{ fontWeight: 'bold' }}>{`Step ${steporder}: ${stepname}`}</Typography>
        <Plot
          data={[
            {
              x: datetimeutc,
              y: rawx,
              type: 'scatter',
              mode: 'lines',
              name: 'RAWX',
            },
            {
              x: datetimeutc,
              y: rawy,
              type: 'scatter',
              mode: 'lines',
              name: 'RAWY',
            },
            {
              x: datetimeutc,
              y: rawz,
              type: 'scatter',
              mode: 'lines',
              name: 'RAWZ',
            },
          ]}
          layout={{
            title: {
              text: `Acceleration Data - Step: ${steporder} - ${stepname}`,
              font: {
                color: 'white'
              }
            },
            xaxis: {
              title: {
                text: 'Time',
                font: {
                  color: 'white'
                }
              },
              tickfont: {
                color: 'white'
              },
              type: 'date',
              tickformat: '%H:%M:%S',
            },
            yaxis: {
              title: {
                text: 'Values',
                font: {
                  color: 'white'
                }
              },
              tickfont: {
                color: 'white'
              }
            },
            plot_bgcolor: 'rgba(0,0,0,0)',
            paper_bgcolor: 'rgba(0,0,0,0)',
            font: {
              color: 'white'
            },
            showlegend: true,
            legend: {
              font: {
                color: 'white'
              },
              x: 1,
              y: 1.2,
              orientation: 'h',
            },
          }}
          useResizeHandler
          style={{ width: '100%', height: '100%' }}
        />
      </Box>
      <Box p={2} border={1} borderColor="grey.400" borderRadius={4} style={classes.plotContainer}>
        <Plot
          data={[
            {
              x: datetimeutc,
              y: rawroll,
              type: 'scatter',
              mode: 'lines',
              name: 'ROLL',
            },
            {
              x: datetimeutc,
              y: rawpitch,
              type: 'scatter',
              mode: 'lines',
              name: 'PITCH',
            },
            {
              x: datetimeutc,
              y: rawyaw,
              type: 'scatter',
              mode: 'lines',
              name: 'YAW',
            },
          ]}
          layout={{
            title: {
              text: `Gyroscope Data - Step: ${steporder} - ${stepname}`,
              font: {
                color: 'white'
              }
            },
            xaxis: {
              title: {
                text: 'Time',
                font: {
                  color: 'white'
                }
              },
              tickfont: {
                color: 'white'
              },
              type: 'date',
              tickformat: '%H:%M:%S',
            },
            yaxis: {
              title: {
                text: 'Values',
                font: {
                  color: 'white'
                }
              },
              tickfont: {
                color: 'white'
              }
            },
            plot_bgcolor: 'rgba(0,0,0,0)',
            paper_bgcolor: 'rgba(0,0,0,0)',
            font: {
              color: 'white'
            },
            showlegend: true,
            legend: {
              font: {
                color: 'white'
              },
              x: 1,
              y: 1.2,
              orientation: 'h',
            },
          }}
          useResizeHandler
          style={{ width: '100%', height: '100%' }}
        />
      </Box>
      <Box p={2} border={1} borderColor="grey.400" borderRadius={4} style={classes.statsContainer}>
        <Box mt={4}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper elevation={3} style={classes.statBox}>
                <Typography variant="body1">{`Sampling Frequency: ${sampling_frequency.toFixed(2)} Hz`}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper elevation={3} style={classes.statBox}>
                <Typography variant="body1">{`Number of Samples per Second: ${num_samples_per_second.toFixed(2)}`}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper elevation={3} style={classes.statBox}>
                <Typography variant="body1">{`Avg Gyro FFT Amplitude: ${avg_gyrofft_amplitude.toFixed(2)}`}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper elevation={3} style={classes.statBox}>
                <Typography variant="body1">{`Avg Gyro FFT Frequency: ${avg_gyrofft_frequency.toFixed(2)}`}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper elevation={3} style={classes.statBox}>
                <Typography variant="body1">{`Avg Accel FFT Amplitude: ${avg_accelfft_amplitude.toFixed(2)}`}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper elevation={3} style={classes.statBox}>
                <Typography variant="body1">{`Avg Accel FFT Frequency: ${avg_accelfft_frequency.toFixed(2)}`}</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}

export default ProtocolView;
